// Pure helpers for the Full Stats widget (FE — #5 backlog). Kept DOM-free so the
// value formatting + key flattening are unit-tested; the widget is a thin view.
//
// Mirrors old Catalyst's Stats tab (soccer-frontend players/stats): one grouped
// dump of every metric for a player-season. The metric catalogue
// (`/player-metric-categories`) provides the grouping + each metric's outputType;
// one `/player-stats` row (grouped by player) provides the values, keyed by
// metric key.

import type { MetricCategory } from "../data/stats.js";

/** Every metric key across all categories/subcategories — the `keys` we request
 *  from POST /player-stats so the returned row carries a value for each cell. */
export function allMetricKeys(categories: MetricCategory[]): string[] {
  return categories.flatMap((c) => c.subcategories.flatMap((s) => s.playerMetrics.map((m) => m.key)));
}

/**
 * Format a metric value for display, honouring its outputType (matches Catalyst's
 * scaleMetricValue + fmtMetricValue):
 *  - percentage: stored as a 0–1 fraction → ×100 with a "%" suffix (0.663 → "66.33%").
 *  - integer: rounded (the engine can return float noise like 769.0000001).
 *  - decimal / anything else: whole numbers as-is, otherwise 2 dp.
 * Null/undefined → an em dash; a non-numeric value passes through as its string.
 */
export function formatMetric(value: number | string | null | undefined, outputType?: string | null): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);

  if (outputType === "percentage") {
    const scaled = n * 100;
    return `${Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(2)}%`;
  }
  if (outputType === "integer") return String(Math.round(n));
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
